
function formatErvStatus(rawVal: any): { text: string; color: string } | null {
  if (rawVal === undefined || rawVal === null || rawVal === '') return null;
  const num = Number(rawVal);
  if (isNaN(num)) {
    const str = String(rawVal).trim();
    if (str === '0' || str === '정지' || str === 'off' || str === 'stop') return { text: '정지', color: 'text-slate-500' };
    if (str === '1' || str === '약풍' || str === 'low') return { text: '약풍', color: 'text-emerald-400' };
    if (str === '2' || str === '중풍' || str === 'medium' || str === 'mid') return { text: '중풍', color: 'text-teal-400' };
    if (str === '3' || str === '강풍' || str === 'high') return { text: '강풍', color: 'text-cyan-400' };
    if (str === '-1' || str === '실패' || str === 'error' || str === 'fail') return { text: '실패', color: 'text-rose-400' };
    return { text: str, color: 'text-emerald-400' };
  }
  switch (num) {
    case 0: return { text: '정지', color: 'text-slate-500' };
    case 1: return { text: '약풍', color: 'text-emerald-400' };
    case 2: return { text: '중풍', color: 'text-teal-400' };
    case 3: return { text: '강풍', color: 'text-cyan-400' };
    case -1: return { text: '실패', color: 'text-rose-400' };
    default: return { text: `${num}`, color: 'text-emerald-400' };
  }
}

import { ComponentType, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  ThermometerIcon,
  ZapIcon,
  WindIcon,
  ChevronRightIcon,
  PersonStandingIcon,
  DropletsIcon } from
'lucide-react';
import {
  getSensorIcon,
  getSensorColorObject,
  getSensorTypeName,
  SensorType } from
'../../utils/sensorUtils';
import { TelemetryData } from '../../hooks/useTelemetry';

interface Sensor {
  id: string;
  type: SensorType;
  label: string;
  alias?: string;
  roomId?: string;
  active: boolean;
  x: number;
  y: number;
  primary_appliance?: string;
}
interface Room {
  id: string;
  name: string;
  temp: number;
  humidity: number;
  co2: number;
  tvoc: number;
  dust: number;
  energy: number;
  icon: ComponentType<{
    className?: string;
  }>;
}
interface SensorListViewProps {
  room: Room;
  sensors: Sensor[];
  telemetry: Record<string, TelemetryData>;
  onSelectSensor: (sensor: Sensor) => void;
}

// ─── Sorting helpers ───
const naturalSort = (a: any, b: any) => {
  if (typeof a !== 'string' || typeof b !== 'string') return 0;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
};

const TYPE_PRIORITY: Record<string, number> = {
  'SEO': 1,
  'SEM': 1,
  'SER': 1,
  'SES': 2,
  'SEL': 3,
  'SEC': 4,
};


// ─── Scoring helpers ───
function scoreTemp(temp: number): number {
  if (temp >= 22 && temp <= 26) return 100;
  if (temp >= 20 && temp < 22 || temp > 26 && temp <= 28) return 70;
  if (temp >= 18 && temp < 20 || temp > 28 && temp <= 30) return 40;
  return 10;
}
function scoreHumidity(h: number): number {
  if (h >= 40 && h <= 60) return 100;
  if (h >= 30 && h < 40 || h > 60 && h <= 70) return 70;
  if (h >= 20 && h < 30 || h > 70 && h <= 80) return 40;
  return 10;
}
function scoreCO2(ppm: number): number {
  if (ppm < 800) return 100;
  if (ppm < 1200) return 70;
  if (ppm < 2000) return 40;
  return 10;
}
function scoreTVOC(mgm3: number): number {
  if (mgm3 < 0.3) return 100;
  if (mgm3 < 0.6) return 70;
  if (mgm3 < 1.0) return 40;
  return 10;
}
function scorePM25(ugm3: number): number {
  if (ugm3 < 15) return 100;
  if (ugm3 < 35) return 70;
  if (ugm3 < 75) return 40;
  return 10;
}
function getAQIGrade(score: number) {
  if (score >= 80)
  return {
    label: '좋음',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/20'
  };
  if (score >= 60)
  return {
    label: '보통',
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/20'
  };
  if (score >= 40)
  return {
    label: '나쁨',
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/20'
  };
  return {
    label: '매우 나쁨',
    color: 'text-red-400',
    bgColor: 'bg-red-500/20'
  };
}
// ─── Component ───
function getEnergyGrade(power: number): {
  label: string;
  color: string;
  bgColor: string;
} {
  if (power < 1) {
    return {
      label: '낮음',
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/20'
    };
  }
  if (power < 5) {
    return {
      label: '보통',
      color: 'text-cyan-400',
      bgColor: 'bg-cyan-500/20'
    };
  }
  if (power < 10) {
    return {
      label: '높음',
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/20'
    };
  }
  return {
    label: '매우 높음',
    color: 'text-red-400',
    bgColor: 'bg-red-500/20'
  };
}
// ─── Component ───

function getComfortScoreLabel(temp: number, humidity: number) {
  const ts = scoreTemp(temp);
  const hs = scoreHumidity(humidity);
  const avg = (ts + hs) / 2;
  if (avg >= 80)
  return {
    label: '쾌적',
    color: 'text-emerald-400'
  };
  if (avg >= 60)
  return {
    label: '양호',
    color: 'text-cyan-400'
  };
  if (avg >= 40)
  return {
    label: '보통',
    color: 'text-amber-400'
  };
  return {
    label: '불쾌',
    color: 'text-red-400'
  };
}
export function SensorListView({
  room,
  sensors,
  telemetry,
  onSelectSensor
}: SensorListViewProps) {
  const [sortMode, setSortMode] = useState<'type' | 'name'>('type');

  const sortedSensors = useMemo(() => {
    return [...sensors].sort((a, b) => {
      if (sortMode === 'type') {
        const priorityA = TYPE_PRIORITY[a.type] || 99;
        const priorityB = TYPE_PRIORITY[b.type] || 99;
        
        if (priorityA !== priorityB) return priorityA - priorityB;
        if (a.type !== b.type) return naturalSort(a.type, b.type);
        return naturalSort(a.label, b.label);
      } else {
        return naturalSort(a.label, b.label);
      }
    });
  }, [sensors, sortMode]);

  if (!room) {
    return null;
  }
  const roomHumidity = room.humidity || 48;
  const roomEnergy = room.energy || 0;
  const energyState = getEnergyGrade(roomEnergy);
  const comfortInfo = getComfortScoreLabel(room.temp, roomHumidity);
  const co2Score = scoreCO2(room.co2 );
  const tvocScore = scoreTVOC(room.tvoc);
  const pm25Score = scorePM25(room.dust);
  const overallAQI = Math.round((co2Score + tvocScore + pm25Score) / 3);
  const aqiGrade = getAQIGrade(overallAQI);
  return (
    <div className="flex-1 overflow-y-auto px-4 pt-3 pb-20 space-y-3 overscroll-contain"
      style={{ background: 'var(--mobile-bg-alt)' }}
    >
      {/* Summary Cards Row */}
      <motion.div
        initial={{
          opacity: 0,
          y: 12
        }}
        animate={{
          opacity: 1,
          y: 0
        }}
        transition={{
          duration: 0.3
        }}
        className="grid grid-cols-3 gap-2.5">
        
        {/* 쾌적성 */}
        <div className="glass-panel rounded-xl p-3 flex flex-col items-center gap-1.5">
          <div className="p-1.5 rounded-lg bg-cyan-500/20">
            <ThermometerIcon className="w-4 h-4 text-cyan-400" />
          </div>
          <span className="text-[0.8rem] text-slate-500 uppercase font-medium font-bold tracking-wider">
            쾌적성
          </span>
          <span className={`text-[0.8rem] font-medium font-bold ${comfortInfo.color}`}>
            {comfortInfo.label}
          </span>
          <div className="flex items-center gap-1 text-[0.8rem] font-mono" style={{ color: 'var(--mobile-text-muted)' }}>
            <span>{room.temp.toFixed(1)}°C</span>
            <span>·</span>
            <span>{roomHumidity.toFixed(0)}%</span>
          </div>
        </div>

        {/* 전기사용량 */}
        <div className="glass-panel rounded-xl p-3 flex flex-col items-center gap-1.5">
          <div className="p-1.5 rounded-lg bg-amber-500/20">
            <ZapIcon className="w-4 h-4 text-amber-400" />
          </div>
          <span className="text-[0.8rem] uppercase tracking-wider font-medium font-bold" style={{ color: 'var(--mobile-text-muted)' }}>
            전기사용량
          </span>
          <span className={`text-[0.8rem] font-medium font-bold ${energyState.color}`}>
            {energyState.label}
          </span>
          <span className="text-[0.8rem] font-bold text-amber-400 font-mono" style={{ color: 'var(--mobile-text-muted)' }}>
            {Number(roomEnergy).toFixed(3)} kWh
          </span>
        </div>

        {/* 종합공기질 */}
        <div className="glass-panel rounded-xl p-3 flex flex-col items-center gap-1.5">
          <div className={`p-1.5 rounded-lg ${aqiGrade.bgColor}`}>
            <WindIcon className={`w-4 h-4 ${aqiGrade.color}`} />
          </div>
          <span className="text-[0.8rem] uppercase tracking-wider font-medium font-bold" style={{ color: 'var(--mobile-text-muted)' }}>
            공기질
          </span>
          <span className={`text-[0.8rem] font-medium font-bold ${aqiGrade.color}`}>
            {aqiGrade.label}
          </span>
          <span className={`text-[0.8rem] font-mono font-bold ${aqiGrade.color}`} style={{ color: 'var(--mobile-text-muted)' }}>
            {overallAQI}점
          </span>
        </div>
      </motion.div>

      {/* Sensor List */}
      <motion.div
        initial={{
          opacity: 0,
          y: 12
        }}
        animate={{
          opacity: 1,
          y: 0
        }}
        transition={{
          duration: 0.3,
          delay: 0.1
        }}>
        
        <div className="flex items-center justify-between mb-2.5 px-1">
          <span className="text-[0.8rem] font-medium font-bold uppercase tracking-wider" style={{ color: 'var(--mobile-text-muted)' }}>
            센서 ({sensors.length}개)
          </span>
          <div className="flex rounded-lg p-0.5" style={{ background: 'var(--mobile-bg-sub)' }}>
            <button 
              onClick={() => setSortMode('type')}
              className={`px-2 py-1 rounded-md text-[0.7rem] font-medium transition-all ${sortMode === 'type' ? 'shadow-sm' : ''}`}
              style={{
                background: sortMode === 'type' ? 'var(--mobile-tab-active)' : 'transparent',
                color: sortMode === 'type' ? 'var(--mobile-text-primary)' : 'var(--mobile-text-muted)'
              }}
            >
              종류순
            </button>
            <button 
              onClick={() => setSortMode('name')}
              className={`px-2 py-1 rounded-md text-[0.7rem] font-medium transition-all ${sortMode === 'name' ? 'shadow-sm' : ''}`}
              style={{
                background: sortMode === 'name' ? 'var(--mobile-tab-active)' : 'transparent',
                color: sortMode === 'name' ? 'var(--mobile-text-primary)' : 'var(--mobile-text-muted)'
              }}
            >
              이름순
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {sortedSensors.map((sensor, i) => {
            const Icon = getSensorIcon(sensor.type);
            const colors = getSensorColorObject(sensor.type);
            const deviceData = telemetry[sensor.id] || {};
            
            const powerVal = deviceData.power_instant?.[0]?.value;
            const tempVal = deviceData.temperature?.[0]?.value;
            const humVal = deviceData.humidity?.[0]?.value;
            const presVal = deviceData.presence_state?.[0]?.value;
            const lightVal = deviceData.light_state?.[0]?.value;
            const curtainVal = deviceData.curtain_state?.[0]?.value;
            const ervVal = deviceData.erv?.[0]?.value ?? deviceData.ERV?.[0]?.value;

            return (
              <motion.button
                key={sensor.id}
                initial={{
                  opacity: 0,
                  x: -12
                }}
                animate={{
                  opacity: 1,
                  x: 0
                }}
                transition={{
                  delay: 0.15 + i * 0.04
                }}
                onClick={() => onSelectSensor(sensor)}
                className="w-full flex items-center gap-3 p-3 rounded-xl glass-panel glass-panel-hover active:opacity-80 transition-colors min-h-[56px]">
                
                {/* Icon */}
                <div
                  className={`p-2 rounded-lg ${colors.bg} ${colors.icon} flex-shrink-0`}>
                  
                  <Icon className="w-4.5 h-4.5" />
                </div>

                {/* Label + Type */}
                <div className="flex-1 text-left min-w-0">
                  <div className="text-[0.9rem] font-medium truncate flex items-center gap-1.5" style={{ color: 'var(--mobile-text-primary)' }}>
                    <span className="truncate">{sensor.label}</span>
                    {sensor.primary_appliance && (
                      <span className="text-[0.8rem] text-cyan-400 font-bold align-middle">
                        {sensor.primary_appliance}
                      </span>
                    )}
                    {sensor.active ? (
                      <motion.span
                        className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-emerald-400"
                        animate={{ opacity: [1, 0.4, 1] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                      />
                    ) : (
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-slate-600" />
                    )}
                  </div>
                  <div className="text-[0.7rem] font-mono" style={{ color: 'var(--mobile-text-muted)' }}>
                    {getSensorTypeName(sensor.type)}
                  </div>
                </div>

                {/* Values */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {(sensor.type === 'SEO' || sensor.type === 'SEM' || sensor.type === 'SER') && powerVal !== undefined &&
                  <span className={`text-[0.9rem] font-mono font-bold ${sensor.type === 'SEM' ? 'text-blue-400' : 'text-violet-400'}`}>
                      {Number(powerVal).toFixed(1)}W
                    </span>
                  }
                  {sensor.type === 'SEL' && lightVal !== undefined &&
                  <span className="text-[0.9rem] font-mono font-bold text-amber-400">
                      {lightVal === '0' ? '꺼짐' : '켜짐'}
                    </span>
                  }
                  {sensor.type === 'SES' && tempVal !== undefined &&
                  <div className="flex items-center gap-1.5 text-sm font-mono">
                      <span className="font-bold text-[0.9rem] text-rose-400">
                        {Number(tempVal).toFixed(1)}°C
                      </span>
                      <span className="text-slate-600">|</span>
                      <span className="font-bold text-[0.9rem] text-cyan-400 flex items-center gap-0.5" >
                        <DropletsIcon className="w-3 h-3" />
                        {Number(humVal).toFixed(1)}%
                      </span>
                    </div>
                  }
                  {sensor.type === 'RADAR' &&
                  <div
                    className={`flex items-center gap-1 text-[0.9rem] font-bold ${presVal === 'true' ? 'text-rose-400' : 'text-slate-600'}`}>
                    
                      <PersonStandingIcon className="w-4 h-4" />
                      <span>{presVal === 'true' ? '재실' : '부재'}</span>
                    </div>
                  }
                  {(sensor.type === 'SEC' || sensor.type === 'CURTAIN' || sensor.type === 'BLIND') && curtainVal !== undefined &&
                  <span className="text-[0.9rem] font-mono font-bold text-orange-400">
                      {curtainVal === '0' ? '열림' : '닫힘'}
                    </span>
                  }
                  {/* Fallback for sensors without data */}
                  {!powerVal && !tempVal && !lightVal && !curtainVal && sensor.type !== 'RADAR' &&
                  <span className="text-xs font-mono text-slate-600"></span>
                  }
                </div>

                {/* Arrow */}
                <ChevronRightIcon className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--mobile-text-muted)' }} />
              </motion.button>);

          })}
        </div>

        {sensors.length === 0 &&
        <div className="text-center py-12 text-sm" style={{ color: 'var(--mobile-text-muted)' }}>
            이 영역에 등록된 센서가 없습니다
          </div>
        }
      </motion.div>
    </div>);

}


